// Browser-side HMAC-SHA256 signer, mirroring server/telemetry/src/hmac.ts.
//
// The backend verifies `x-wd-signature` = lowercase-hex HMAC-SHA256 over
// `${timestamp}.` + body, with `x-wd-timestamp` = unix-ms. For read requests
// the body is empty. We reuse the browser's SubtleCrypto so no dependency is
// needed.
//
// NOTE: the current backend only HMAC-verifies upload/diagnostic/live, NOT the
// read endpoints (GET /trips, /trips/:id/report). Signing reads is therefore
// belt-and-braces today; it becomes load-bearing if/when read auth is added.
// See api.ts for the auth-header wiring.

const encoder = new TextEncoder();

function toHex(bytes: Uint8Array): string {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}

async function importKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
}

/** Lowercase-hex HMAC over `${timestamp}.` + body (empty string for GET). */
export async function signHmac(body: string, timestamp: string, secret: string): Promise<string> {
  const key = await importKey(secret);
  const message = encoder.encode(`${timestamp}.${body}`);
  const sig = await crypto.subtle.sign("HMAC", key, message);
  return toHex(new Uint8Array(sig));
}

export interface AuthHeaders {
  "x-wd-timestamp": string;
  "x-wd-signature": string;
}

/** Build the signed auth headers for a request body (default empty). */
export async function authHeaders(secret: string, body = ""): Promise<AuthHeaders> {
  const timestamp = String(Date.now());
  const signature = await signHmac(body, timestamp, secret);
  return { "x-wd-timestamp": timestamp, "x-wd-signature": signature };
}
